import { buildEmbedSrc } from './utils';

( function () {
	const FB_MIN_WIDTH = 180;
	const FB_MAX_WIDTH = 500;

	function clampWidth( width ) {
		return Math.max( FB_MIN_WIDTH, Math.min( FB_MAX_WIDTH, Math.round( width ) ) );
	}

	function readAttrs( wrapper ) {
		const pageUrl    = wrapper.dataset.pageUrl || '';
		const tabs       = ( wrapper.dataset.tabs || 'timeline' ).split( ',' );
		const dataHeight = parseInt( wrapper.dataset.height, 10 );
		// data-height="0" means full-height mode — measure the live rendered height.
		const height       = dataHeight || wrapper.clientHeight || 500;
		const smallHeader  = wrapper.dataset.smallHeader  === '1';
		const hideCover    = wrapper.dataset.hideCover    === '1';
		const showFacepile = wrapper.dataset.showFacepile === '1';

		return { pageUrl, tabs, height, smallHeader, hideCover, showFacepile };
	}

	function createIframe( wrapper ) {
		// Never create a second iframe for a wrapper that already has one.
		const existing = wrapper.querySelector( 'iframe' );
		if ( existing ) {
			return existing;
		}

		const attrs = readAttrs( wrapper );
		const width = clampWidth( wrapper.clientWidth || 500 );
		const src = buildEmbedSrc( { ...attrs, width } );
		if ( ! src ) {
			return null;
		}

		const iframe = document.createElement( 'iframe' );
		iframe.src = src;
		iframe.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;border:none;';
		iframe.setAttribute( 'loading', 'lazy' );
		iframe.setAttribute( 'allowfullscreen', '' );
		iframe.setAttribute( 'frameborder', '0' );
		iframe.setAttribute( 'scrolling', 'no' );
		iframe.setAttribute( 'allow', 'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share' );
		iframe.setAttribute( 'title', 'Facebook Page Embed' );

		// Facebook's anonymous Page Plugin can render blank with no error, which we
		// can't detect across origins — but if the iframe fails outright (network
		// error), make sure the fallback link is visually emphasized.
		iframe.addEventListener( 'error', function () {
			const fallback = wrapper.querySelector( '.fb-embed-fallback-link' );
			if ( fallback ) {
				fallback.style.fontSize = '16px';
				fallback.style.padding = '14px 10px';
			}
		} );

		const skeleton = wrapper.querySelector( '.fb-embed-skeleton' );
		if ( skeleton ) {
			wrapper.replaceChild( iframe, skeleton );
		} else {
			wrapper.insertBefore( iframe, wrapper.firstChild );
		}

		iframe.dataset.fbWidth = String( width );

		return iframe;
	}

	function attachResponsiveObserver( wrapper, iframe ) {
		let debounceTimer = null;
		let lastWidth = clampWidth( parseInt( iframe.dataset.fbWidth, 10 ) || wrapper.clientWidth || 500 );

		const ro = new ResizeObserver( () => {
			clearTimeout( debounceTimer );
			debounceTimer = setTimeout( () => {
				const newWidth = clampWidth( wrapper.clientWidth || 500 );
				// Skip the guaranteed first callback (observe() fires immediately) and
				// any resize that doesn't actually change the effective (clamped) width —
				// this is what prevents a reload loop.
				if ( newWidth === lastWidth ) {
					return;
				}
				lastWidth = newWidth;

				const attrs = readAttrs( wrapper );
				const newSrc = buildEmbedSrc( { ...attrs, width: newWidth } );
				if ( newSrc ) {
					iframe.src = newSrc;
				}
			}, 300 );
		} );

		ro.observe( wrapper );
		return ro;
	}

	function initBlock( wrapper ) {
		// Guard against running twice on the same block instance.
		if ( wrapper.dataset.fbInitialized === '1' ) {
			return;
		}
		wrapper.dataset.fbInitialized = '1';

		const isLazy       = wrapper.dataset.lazy       === '1';
		const isResponsive = wrapper.dataset.responsive === '1';

		if ( isLazy ) {
			const io = new IntersectionObserver(
				( entries, observer ) => {
					entries.forEach( ( entry ) => {
						if ( ! entry.isIntersecting ) return;
						observer.unobserve( wrapper );
						const iframe = createIframe( wrapper );
						if ( iframe && isResponsive ) {
							attachResponsiveObserver( wrapper, iframe );
						}
					} );
				},
				{ rootMargin: '200px' }
			);
			io.observe( wrapper );
		} else {
			const iframe = createIframe( wrapper );
			if ( iframe && isResponsive ) {
				attachResponsiveObserver( wrapper, iframe );
			}
		}
	}

	function init() {
		document
			.querySelectorAll( '.wp-block-gandaki-govt-facebook-page-embed' )
			.forEach( initBlock );
	}

	// The viewScript can execute before or after DOMContentLoaded depending on how
	// WordPress schedules/loads it — relying solely on the event means init() never
	// runs if the script finishes loading after the event has already fired.
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init, { once: true } );
	} else {
		init();
	}
} )();
